import PermissionButton from 'components/buttons/PermissionButton';
import ListCreateButton from 'components/buttons/ListCreateButton';
import Loader from 'components/Loader';
import AssignmentModal from 'components/modals/assignment/AssignmentModal';
import { useModal } from 'components/modals/useModal';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useAddMatterAssignmentMutation,
    useDeleteMatterAssignmentMutation,
    useLazyGetAssignementsWithFiltersQuery,
    useLazyGetMatterAssignmentDeleteValidationQuery,
    useUpdateMatterAssignmentMutation,
} from 'store/api/assignments';
import {
    AssignmentDeleteValidation,
    MatterAssignmentCreateDto,
    MatterAssignmentDto,
    MatterAssignmentForm,
} from 'types/assignments';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { MatterDto } from 'types/matters';
import { errorAlert, warningAlert } from 'utils/alert';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { buildAssignmentDeleteBlockerMessage } from './assignmentDeleteValidationMessage';
import { AssignementStatusLabel } from './AssignmentStatusLabel';

const AssignmentsTable = ({
    matter,
    classifiers,
    passiveFilter = '',
}: {
    matter: MatterDto;
    classifiers: Record<string, ClassifierData>;
    passiveFilter?: string;
}) => {
    const { openModal, closeModal } = useModal();
    const hasPermissionToUpdateAssignments = useHasPermissions(['UPDATE.MATTER_ASSIGNMENT']);
    const [fetchData, { data, isFetching }] = useLazyGetAssignementsWithFiltersQuery();
    const [deleteMatterAssignment] = useDeleteMatterAssignmentMutation();
    const [fetchDeleteValidation] = useLazyGetMatterAssignmentDeleteValidationQuery();
    const [addMatterAssignment, { isLoading: isLoadingAddAssignment }] = useAddMatterAssignmentMutation();
    const [updateMatterAssignment, { isLoading: isLoadingUpdateAssignment }] = useUpdateMatterAssignmentMutation();
    const matterId = matter.id;
    const isNonBillableProjectOrCase = useMemo(
        () => matter.project?.isNonBillable ?? matter.isNonBillable,
        [matter.project?.isNonBillable, matter.isNonBillable]
    );

    const isNonBillableHoursPreserved = useMemo(() => {
        if (!isNonBillableProjectOrCase) return false;

        return matter.project?.preserveNonBillableHours ?? matter.preserveNonBillableHours;
    }, [isNonBillableProjectOrCase, matter.project?.preserveNonBillableHours, matter.preserveNonBillableHours]);

    const handleAssignmentCreateOrUpdate = useCallback(
        async (assignment: MatterAssignmentCreateDto, id?: number | null) => {
            try {
                if (id) {
                    updateMatterAssignment({ ...assignment, id })
                        .unwrap()
                        .then(() => closeModal())
                        .catch((error) => {
                            console.error(error);
                        });
                } else {
                    addMatterAssignment({ ...assignment })
                        .unwrap()
                        .then(() => closeModal())
                        .catch((error) => {
                            console.error(error);
                        });
                }
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addMatterAssignment, closeModal, updateMatterAssignment]
    );

    const openAssignmentModal = useCallback(
        (action: 'add' | 'edit', assignment?: MatterAssignmentDto): void => {
            const initalData: MatterAssignmentForm = {
                id: assignment?.id,
                name: assignment?.name ?? '',
                description: assignment?.description ?? '',
                startDate: assignment?.startDate
                    ? formatDateStringInput(new Date(assignment?.startDate))
                    : formatDateStringInput(new Date()),
                endDate: assignment?.endDate ? formatDateStringInput(new Date(assignment?.endDate)) : null,
                isNonBillable: action === 'add' ? isNonBillableProjectOrCase : (assignment?.isNonBillable ?? false),
                preserveNonBillableHours:
                    action === 'add' ? isNonBillableHoursPreserved : (assignment?.preserveNonBillableHours ?? false),
                capLimit: assignment?.capLimit ?? '',
                currencyTypeCode: '',
                matterId: assignment?.matterId ?? matterId,
            };
            const isReadOnly: boolean = !!assignment && !hasPermissionToUpdateAssignments;

            openModal(
                <AssignmentModal
                    onChange={(value) => {
                        void handleAssignmentCreateOrUpdate(value, assignment?.id);
                    }}
                    isNonBillableProjectOrCase={isNonBillableProjectOrCase}
                    onDeleteAssignment={async () => {
                        if (!assignment?.id) {
                            return;
                        }
                        let validation: AssignmentDeleteValidation;
                        try {
                            validation = await fetchDeleteValidation({ id: assignment.id }).unwrap();
                        } catch (e) {
                            console.error('assignment delete-validation fetch failed: ', e);
                            errorAlert(t('assignments.error-checking-deletability'));
                            return;
                        }
                        const blockerMessage = buildAssignmentDeleteBlockerMessage(validation);
                        if (blockerMessage) {
                            warningAlert(blockerMessage);
                            return;
                        }
                        await deleteMatterAssignment({ id: assignment.id });
                        closeModal();
                    }}
                    initialData={initalData}
                    onClose={closeModal}
                    classifiers={classifiers}
                    titleId={assignment ? 'assignments.edit-assignment' : 'assignments.add-new-assignment'}
                    type={assignment ? 'edit' : 'create'}
                    readOnly={isReadOnly}
                />,
                closeModal
            );
        },
        [
            classifiers,
            closeModal,
            deleteMatterAssignment,
            fetchDeleteValidation,
            handleAssignmentCreateOrUpdate,
            openModal,
            matterId,
            hasPermissionToUpdateAssignments,
            isNonBillableProjectOrCase,
            isNonBillableHoursPreserved,
        ]
    );

    const columns = useMemo<DataTableColumnDef<MatterAssignmentDto>[]>(
        () => [
            {
                header: t('name'),
                accessorKey: 'name',
                id: 'name',
                meta: {
                    widthWeight: 2,
                    minPx: 160,
                },
                cell: ({ cell }) => {
                    const { name } = cell.row.original;

                    return (
                        <div className="flex flex-col">
                            <p className="text-body1 font-bold">{name}</p>
                        </div>
                    );
                },
            },
            {
                header: t('assignments.status'),
                id: 'status',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ row }) => {
                    return (
                        <div className="flex gap-1">
                            <AssignementStatusLabel
                                classifier={classifiers['ASSIGNMENT_STATUS_TYPE']}
                                endDate={row.original.endDate}
                            />
                        </div>
                    );
                },
            },
            {
                header: t('description'),
                accessorKey: 'description',
                enableSorting: false,
                meta: {
                    widthWeight: 4,
                    minPx: 280,
                },
                cell: ({ cell }) => {
                    const { description } = cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1">{description}</p>
                        </div>
                    );
                },
            },
            {
                header: t('cap'),
                id: 'cap',
                enableSorting: false,
                meta: {
                    widthWeight: 0.7,
                    minPx: 90,
                },
                cell: ({ row }) => {
                    const { capLimit } = row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1">{capLimit}</p>
                        </div>
                    );
                },
            },
            {
                header: t('assignments.opening-date-table'),
                id: 'assignmentOpeningDate',
                accessorKey: 'startDate',
                meta: {
                    sortAccessor: 'startDate',
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ row }) => {
                    const { startDate } = row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1 whitespace-nowrap">{startDate}</p>
                        </div>
                    );
                },
            },
            {
                header: t('assignments.closing-date-table'),
                id: 'assignentClosingDate',
                accessorKey: 'endDate',
                meta: {
                    sortAccessor: 'endDate',
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ row }) => {
                    const { endDate } = row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1 whitespace-nowrap">{endDate}</p>
                        </div>
                    );
                },
            },
            {
                header: '',
                id: 'edit',
                enableSorting: false,
                meta: {
                    // Fixed column (single edit/view button) — excluded from the weighted flex.
                    fixedPx: 100,
                },
                cell: ({ row }) => {
                    const textId = !hasPermissionToUpdateAssignments ? t('assignments.view') : t('assignments.edit');
                    const editButtonPermissions = ['UPDATE.MATTER_ASSIGNMENT'];
                    return (
                        <div className="flex flex-nowrap items-center justify-end">
                            <PermissionButton
                                permissions={editButtonPermissions}
                                variant="outlined"
                                size="extraSmall"
                                onClick={() => {
                                    openAssignmentModal('edit', row.original);
                                }}
                            >
                                <FormattedMessage id={textId} />
                            </PermissionButton>
                        </div>
                    );
                },
            },
        ],
        [openAssignmentModal, classifiers, hasPermissionToUpdateAssignments]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'description' }, { selector: 'name' }],
            passiveFilter: matterId ? `matter.id=in=(${matterId})` : passiveFilter,
        }),
        [passiveFilter, matterId]
    );

    if (isLoadingUpdateAssignment || isLoadingAddAssignment) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<MatterAssignmentDto>
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="assignments"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="assignments.assignment"
            singularTextId="assignments.assignments"
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState
            canRememberFilters={false}
            extraHeaderComponent={
                matterId ? (
                    <ListCreateButton
                        permissions={['CREATE.MATTER_ASSIGNMENT']}
                        requiredFeature={FeatureFlag.CASE_ASSIGNMENT_ENABLED}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => openAssignmentModal('add')}
                        labelId="assignments.add-new-assignment"
                        newLabelId="new-entity.assignment"
                    />
                ) : null
            }
        />
    );
};

export default AssignmentsTable;
